import type { SalesComparisonColumns, SalesComparisonGridColumnData } from '../types.ts';
import { getSalesComparisonNestedValue } from './getSalesComparisonNestedValue.ts';

type GlossaryDef = {
  sort: number;
  line: string;
  matches: (v: string) => boolean;
};

const word = (s: string) => {
  const esc = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${esc}\\b`, 'i');
};

const GLOSSARY_DEFS: GlossaryDef[] = [
  { sort: 10, line: 'B = Beneficial', matches: (v) => v.startsWith('B;') },
  { sort: 11, line: 'N = Neutral', matches: (v) => v.startsWith('N;') },
  { sort: 12, line: 'A = Adverse', matches: (v) => v.startsWith('A;') },

  {
    sort: 200,
    line: 'Agric = Agricultural',
    matches: (v) => word('Agric').test(v) || /\bAgricultural\b/i.test(v),
  },
  {
    sort: 201,
    line: 'Arpt = Airport',
    matches: (v) => word('Arpt').test(v) || /\bAirport\b/i.test(v),
  },
  {
    sort: 202,
    line: 'BdyWtr = Body of Water',
    matches: (v) =>
      word('BdyWtr').test(v) ||
      /\bBody of Water\b/i.test(v) ||
      /\bBody Of Water\b/i.test(v) ||
      /\bBody Water\b/i.test(v),
  },
  {
    sort: 203,
    line: 'BsyRdwy = Busy Roadway',
    matches: (v) => word('BsyRdwy').test(v) || /\bBusy Roadway\b/i.test(v),
  },
  {
    sort: 204,
    line: 'CommAr = Commercial Area',
    matches: (v) => word('CommAr').test(v) || /\bCommercial Area\b/i.test(v),
  },
  {
    sort: 205,
    line: 'GlfCrs = Golf Course',
    matches: (v) => word('GlfCrs').test(v) || /\bGolf Course\b/i.test(v),
  },
  {
    sort: 206,
    line: 'GrnSpc = Green Space',
    matches: (v) => word('GrnSpc').test(v) || /\bGreen Space\b/i.test(v),
  },
  {
    sort: 207,
    line: 'HiDensRes = High Density Residential',
    matches: (v) => word('HiDensRes').test(v) || /\bHigh Density Residential\b/i.test(v),
  },
  {
    sort: 208,
    line: 'HiPresGasLn = High Pressure Gas Line',
    matches: (v) => word('HiPresGasLn').test(v) || /\bHigh Pressure Gas Line\b/i.test(v),
  },
  {
    sort: 209,
    line: 'HistDist = Historic District',
    matches: (v) => word('HistDist').test(v) || /\bHistoric District\b/i.test(v),
  },
  {
    sort: 210,
    line: 'IndAr = Industrial Area',
    matches: (v) => word('IndAr').test(v) || /\bIndustrial Area\b/i.test(v),
  },
  {
    sort: 211,
    line: 'LclDistLn = Local Distribution Line',
    matches: (v) => word('LclDistLn').test(v) || /\bLocal Distribution Line\b/i.test(v),
  },
  {
    sort: 212,
    line: 'OilGasWl = Oil or Gas Well',
    matches: (v) =>
      word('OilGasWl').test(v) || /\bOil or Gas Well\b/i.test(v) || /\bOil Or Gas Well\b/i.test(v),
  },
  {
    sort: 213,
    line: 'OHPwrLn = Overhead Power Line',
    matches: (v) => word('OHPwrLn').test(v) || /\bOverhead Power Line\b/i.test(v),
  },
  {
    sort: 214,
    line: 'Park = Park',
    matches: (v) => word('Park').test(v) && !/\bParking\b/i.test(v) && !/\bParking Lot\b/i.test(v),
  },
  {
    sort: 215,
    line: 'PubTrnsHub = Public Transportation Hub',
    matches: (v) =>
      word('PubTrnsHub').test(v) ||
      word('PubTrnHub').test(v) ||
      /\bPublic Transportation Hub\b/i.test(v),
  },
  {
    sort: 216,
    line: 'RlLn = Rail Line',
    matches: (v) => word('RlLn').test(v) || /\bRail Line\b/i.test(v),
  },
  {
    sort: 217,
    line: 'Res = Residential',
    matches: (v) =>
      word('Res').test(v) ||
      (/\bResidential\b/i.test(v) && !/\bHigh Density Residential\b/i.test(v)),
  },
  { sort: 218, line: 'Sch = School', matches: (v) => word('Sch').test(v) || /\bSchool\b/i.test(v) },
  {
    sort: 219,
    line: 'StrmwtrRtn = Stormwater Retention',
    matches: (v) => word('StrmwtrRtn').test(v) || /\bStormwater Retention\b/i.test(v),
  },

  { sort: 300, line: 'Bay = Bay', matches: (v) => word('Bay').test(v) },
  { sort: 301, line: 'Canal = Canal', matches: (v) => word('Canal').test(v) },
  {
    sort: 302,
    line: 'CtyStr = City Street',
    matches: (v) => word('CtyStr').test(v) || /\bCity Street\b/i.test(v),
  },
  {
    sort: 303,
    line: 'Comm = Commercial',
    matches: (v) =>
      word('Comm').test(v) && !/\bCommercial Area\b/i.test(v) && !/\bCity Street\b/i.test(v),
  },
  { sort: 304, line: 'Cove = Cove', matches: (v) => word('Cove').test(v) },
  { sort: 305, line: 'Creek = Creek', matches: (v) => word('Creek').test(v) },
  { sort: 306, line: 'Gulf = Gulf', matches: (v) => word('Gulf').test(v) },
  {
    sort: 307,
    line: 'Hwy = Highway',
    matches: (v) => word('Hwy').test(v) || /\bHighway\b/i.test(v),
  },
  {
    sort: 308,
    line: 'Ind = Industrial',
    matches: (v) => word('Ind').test(v) && !/\bIndustrial Area\b/i.test(v),
  },
  { sort: 309, line: 'Lake = Lake', matches: (v) => word('Lake').test(v) },
  { sort: 310, line: 'Marsh = Marsh', matches: (v) => word('Marsh').test(v) },
  {
    sort: 311,
    line: 'Mtn = Mountain',
    matches: (v) => word('Mtn').test(v) || /\bMountain\b/i.test(v),
  },
  { sort: 312, line: 'Ocean = Ocean', matches: (v) => word('Ocean').test(v) },
  {
    sort: 313,
    line: 'PrkngLt = Parking Lot',
    matches: (v) => word('PrkngLt').test(v) || /\bParking Lot\b/i.test(v),
  },
  {
    sort: 314,
    line: 'Pstrl = Pastoral',
    matches: (v) => word('Pstrl').test(v) || /\bPastoral\b/i.test(v),
  },
  { sort: 315, line: 'Pond = Pond', matches: (v) => word('Pond').test(v) },
  {
    sort: 316,
    line: 'Rsrvr = Reservoir',
    matches: (v) => word('Rsrvr').test(v) || /\bReservoir\b/i.test(v),
  },
  { sort: 317, line: 'River = River', matches: (v) => word('River').test(v) },
  {
    sort: 318,
    line: 'Skyline = Skyline',
    matches: (v) => word('Skyln').test(v) || /\bSkyline\b/i.test(v),
  },
  { sort: 319, line: 'Sound = Sound', matches: (v) => word('Sound').test(v) },
  {
    sort: 320,
    line: 'TrfcWB = Traffic Wall Barriers',
    matches: (v) => word('TrfcWB').test(v) || /\bTraffic Wall Barriers\b/i.test(v),
  },
  { sort: 321, line: 'Valley = Valley', matches: (v) => word('Valley').test(v) },
  { sort: 322, line: 'Woods = Woods', matches: (v) => word('Woods').test(v) },

  { sort: 60, line: 'AG = Attached Garage', matches: (v) => /\d+\s+AG\b/.test(v) },
  { sort: 61, line: 'DG = Detached Garage', matches: (v) => /\d+\s+DG\b/.test(v) },
  {
    sort: 62,
    line: 'C = Carport',
    matches: (v) => v.split(';').some((part) => /^\d+\s+C$/.test(part.trim())),
  },

  { sort: 70, line: 'DK = Deck', matches: (v) => /\bDK\b/.test(v) },
  { sort: 71, line: 'PAT = Patio', matches: (v) => /\bPAT\b/.test(v) },
  { sort: 72, line: 'PCH = Porch', matches: (v) => /\bPCH\b/.test(v) },
];

export const buildSalesComparisonGlossaryText = (
  filteredRows: SalesComparisonColumns,
  properties: SalesComparisonGridColumnData[],
): string | null => {
  const values = filteredRows.flatMap((row) =>
    row.key.flatMap((fieldPath) =>
      properties.flatMap((p) => {
        const cell = getSalesComparisonNestedValue(p, fieldPath);
        return cell ? [cell] : [];
      }),
    ),
  );

  const entries: { sort: number; line: string }[] = [];
  const seen = new Set<string>();

  GLOSSARY_DEFS.forEach((def) => {
    const hit = values.some((v) => def.matches(v));
    if (hit && !seen.has(def.line)) {
      seen.add(def.line);
      entries.push({ sort: def.sort, line: def.line });
    }
  });

  if (entries.length === 0) return null;

  entries.sort((a, b) => a.sort - b.sort);
  return entries.map((e) => e.line).join(' | ');
};
